import {
  at,
  find,
  flow,
  get,
  identity,
  isEqual,
  isNil,
  join,
  map,
  omit,
  toString,
} from 'lodash/fp';

import getMapper, { cleanEmptyValues, mapPath } from 'helpers/mapper';
import { options as adSellerTypeOptions } from 'helpers/fieldOptions/ad/sellerType';
import {
  byId as adSortsById,
  GEOLOCATION_DISTANCE_ASC,
} from 'helpers/fieldOptions/ad/sort';
import { byId as airbagById } from 'helpers/fieldOptions/car/airbag';
import { byId as additionalFeaturesById } from 'helpers/fieldOptions/car/additional';
import { byId as lightById } from 'helpers/fieldOptions/car/light';
import { byId as multimediaById } from 'helpers/fieldOptions/car/multimedia';
import { byId as comfortById } from 'helpers/fieldOptions/car/comfort';
import { byId as seatComfortById } from 'helpers/fieldOptions/car/seatComfort';
import { byId as carColorsById } from 'helpers/fieldOptions/car/color';
import { byId as carExtrasById } from 'helpers/fieldOptions/car/extra';
import { byId as carFuelTypesById } from 'helpers/fieldOptions/car/fuelType';
import { byId as carInteriorsById } from 'helpers/fieldOptions/car/interior';
import { byId as carParkingSensorsById } from 'helpers/fieldOptions/car/parkingSensor';
import { byId as carSecurityById } from 'helpers/fieldOptions/car/security';
import { byId as carServiceHistoryById } from 'helpers/fieldOptions/car/serviceHistory';
import { byId as carTransmissionsById } from 'helpers/fieldOptions/car/transmission';
import { byId as upholsteryById } from 'helpers/fieldOptions/car/upholstery';
import { decodeName } from 'helpers/fieldOptions';
import { inputBoxesToArray } from 'components/forms/InputBoxes';
import {
  ADDITIONAL_NAME,
  AIRBAG_NAME,
  BODY_TYPE_NAME,
  COLOR_NAME,
  COMFORT_NAME,
  CONSUMPTION_RANGE_NAME,
  DISTANCE_NAME,
  DOOR_RANGE_NAME,
  EMISSION_STICKER_NAME,
  ENV_STANDARD_NAME,
  EXTRAS_NAME,
  FUEL_TYPE_NAME,
  GEOLOCATION_NAME,
  HAS_IMAGES_NAME,
  INTERIOR_NAME,
  LIGHT_NAME,
  LOCALE_NAME,
  MAKE_NAME,
  METALLIC_NAME,
  MILEAGE_RANGE_NAME,
  MODEL_NAME,
  MONTHLY_PRICE_RANGE_NAME,
  MULTIMEDIA_NAME,
  NOT_DAMAGED_NAME,
  PAGE_NAME,
  PAGE_SIZE_NAME,
  PARKING_SENSORS_NAME,
  POWER_RANGE_NAME,
  PREV_OWNER_NAME,
  PRICE_RANGE_NAME,
  PRICE_TYPE_NAME,
  SEAT_COMFORT_NAME,
  SEAT_RANGE_NAME,
  SECURITY_NAME,
  SELLER_ID_NAME,
  SELLER_TYPE_NAME,
  SERVICE_HISTORY_NAME,
  SMOKER_NAME,
  SORT_NAME,
  SUB_TYPE_NAME,
  TRANSMISSION_NAME,
  UPHOLSTERY_NAME,
  VEHICLE_NAME,
  YEAR_RANGE_NAME,
} from '../../constants';

const getCheckboxList = (path, getter = identity) =>
  flow(get(path), inputBoxesToArray(decodeName), map(getter));

const getByKey = (path, getter = identity) => flow(get(path), getter);
const mapByKey = (mapping) => (key) => get([key], mapping);

const movePath = (path, getter = get, ...args) =>
  mapPath(path, getter(path, ...args));

// Look the make up before building anything: an unresolved one used to leak
// `undefined` into the joins and serialise as `model=.A4`.
const getBrand = (carMakeById) => () =>
  flow(get([VEHICLE_NAME, '0', MAKE_NAME]), (make) => {
    const brand = carMakeById[make];

    if (brand) {
      return [brand];
    }
  });

const getModel = (carMakeById) => () =>
  flow(
    get([VEHICLE_NAME, '0']),
    at([MAKE_NAME, MODEL_NAME]),
    ([make, model]) => {
      const brand = carMakeById[make];

      if (brand && model) {
        return [join('.', [brand, model.replace('.', '_')])];
      }
    },
  );

const getSubType = (carMakeById) => () =>
  flow(
    get([VEHICLE_NAME, '0']),
    at([MAKE_NAME, MODEL_NAME, SUB_TYPE_NAME]),
    ([make, model, subType]) => {
      const brand = carMakeById[make];

      if (brand && model && subType) {
        return [
          join('.', [
            brand,
            model.replace('.', '_'),
            subType.replace('.', '_'),
          ]),
        ];
      }
    },
  );

const getSort = () =>
  flow(at([GEOLOCATION_NAME, SORT_NAME]), ([geoLocation, sort]) => {
    const [lat, lng] = at(['lat', 'lng'], geoLocation);

    if (isNil(sort)) {
      return;
    }

    const options =
      isNil(lat) && isNil(lng)
        ? omit([GEOLOCATION_DISTANCE_ASC], adSortsById)
        : adSortsById;

    return get([sort], options);
  });

const getPage = () =>
  flow(get([PAGE_NAME]), (page) => {
    if (isNil(page) || String(page) === '1') {
      return;
    }

    return page;
  });

const getProvider = () =>
  flow(
    get([SELLER_TYPE_NAME]),
    (value) =>
      find((option) => isEqual(option.value, value))(adSellerTypeOptions),
    get(['provider']),
  );

const toReadableBool = (value) => {
  if (value === false) {
    return 'no';
  } else if (value === true) {
    return 'yes';
  }

  return undefined;
};

export default (carMakeById = {}) =>
  (source) =>
    getMapper(
      [
        movePath([AIRBAG_NAME], getByKey, mapByKey(airbagById)),
        movePath(
          [ADDITIONAL_NAME],
          getCheckboxList,
          mapByKey(additionalFeaturesById),
        ),
        movePath([BODY_TYPE_NAME], getCheckboxList),
        movePath(['brand'], getBrand(carMakeById)),
        movePath([COLOR_NAME], getCheckboxList, mapByKey(carColorsById)),
        movePath([COMFORT_NAME], getCheckboxList, mapByKey(comfortById)),
        mapPath([DISTANCE_NAME], get([GEOLOCATION_NAME, 'distance'])),
        mapPath(['consumptionMin'], get([CONSUMPTION_RANGE_NAME, 'min'])),
        mapPath(['consumptionMax'], get([CONSUMPTION_RANGE_NAME, 'max'])),
        mapPath(['doorMin'], get([DOOR_RANGE_NAME, 'min'])),
        mapPath(['doorMax'], get([DOOR_RANGE_NAME, 'max'])),
        movePath([EMISSION_STICKER_NAME]),
        movePath([ENV_STANDARD_NAME]),
        movePath([EXTRAS_NAME], getCheckboxList, mapByKey(carExtrasById)),
        movePath([FUEL_TYPE_NAME], getCheckboxList, mapByKey(carFuelTypesById)),
        movePath([INTERIOR_NAME], getCheckboxList, mapByKey(carInteriorsById)),
        movePath([METALLIC_NAME], getCheckboxList),
        mapPath(['lat'], get([GEOLOCATION_NAME, 'lat'])),
        mapPath(['lng'], get([GEOLOCATION_NAME, 'lng'])),
        mapPath(['city'], get([GEOLOCATION_NAME, 'city'])),
        movePath([LIGHT_NAME], getCheckboxList, mapByKey(lightById)),
        mapPath(['mileageMin'], get([MILEAGE_RANGE_NAME, 'min'])),
        mapPath(['mileageMax'], get([MILEAGE_RANGE_NAME, 'max'])),
        movePath(['model'], getModel(carMakeById)),
        movePath([MULTIMEDIA_NAME], getCheckboxList, mapByKey(multimediaById)),
        movePath(['subType'], getSubType(carMakeById)),
        movePath([PAGE_NAME], getPage),
        movePath([PAGE_SIZE_NAME]),
        movePath(
          [PARKING_SENSORS_NAME],
          getCheckboxList,
          mapByKey(carParkingSensorsById),
        ),
        mapPath(['powerMin'], get([POWER_RANGE_NAME, 'min'])),
        mapPath(['powerMax'], get([POWER_RANGE_NAME, 'max'])),
        mapPath(['prevOwnerMax'], get([PREV_OWNER_NAME, 'max'])),
        movePath([PRICE_TYPE_NAME]),
        mapPath(['priceMin'], get([PRICE_RANGE_NAME, 'min'])),
        mapPath(['priceMax'], get([PRICE_RANGE_NAME, 'max'])),
        mapPath(['monthlyPriceMin'], get([MONTHLY_PRICE_RANGE_NAME, 'min'])),
        mapPath(['monthlyPriceMax'], get([MONTHLY_PRICE_RANGE_NAME, 'max'])),
        movePath(['provider'], getProvider),
        movePath(
          [SEAT_COMFORT_NAME],
          getCheckboxList,
          mapByKey(seatComfortById),
        ),
        mapPath(['seatMin'], get([SEAT_RANGE_NAME, 'min'])),
        mapPath(['seatMax'], get([SEAT_RANGE_NAME, 'max'])),
        movePath([SECURITY_NAME], getCheckboxList, mapByKey(carSecurityById)),
        movePath([SELLER_ID_NAME]),
        movePath(
          [SERVICE_HISTORY_NAME],
          getCheckboxList,
          mapByKey(carServiceHistoryById),
        ),
        movePath([SORT_NAME], getSort),
        movePath(
          [TRANSMISSION_NAME],
          getCheckboxList,
          mapByKey(carTransmissionsById),
        ),
        movePath([UPHOLSTERY_NAME], getCheckboxList, mapByKey(upholsteryById)),
        mapPath(['yearMin'], flow(get([YEAR_RANGE_NAME, 'min']), toString)),
        mapPath(['yearMax'], flow(get([YEAR_RANGE_NAME, 'max']), toString)),
        mapPath(['notDamaged'], flow(get([NOT_DAMAGED_NAME]), toReadableBool)),
        mapPath(['smoker'], flow(get([SMOKER_NAME]), toReadableBool)),
        mapPath(
          ['hasImages'],
          flow(
            get([HAS_IMAGES_NAME]),
            (value) => value || undefined,
            toReadableBool,
          ),
        ),
        movePath([LOCALE_NAME]),
        cleanEmptyValues,
      ],
      {},
    )()(source);
